<template>
    <div class="tw-grid tw-grid-cols-12 tw-gap-4">
        <!-- Main Content - Calendar/List View -->
        <div class="tw-col-span-12 lg:tw-col-span-9">
            <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200 tw-min-h-[600px]">
                <!-- Header Section -->
                <div class="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-4 tw-mb-6">
                    <div>
                        <h1 v-if="!loading" class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-1">Meetings</h1>
                        <div v-else class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-32 tw-mb-1 tw-animate-pulse"></div>
                        
                        <p v-if="!loading" class="tw-text-sm tw-text-gray-600">{{ totalMeetings || meetings.length }} meetings total</p>
                        <div v-else class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
                    </div>
                    
                    <!-- Header Actions -->
                    <div v-if="!loading" class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        <!-- Search -->
                        <div class="tw-min-w-[250px]">
                            <FormField
                                v-model="searchQuery"
                                type="text"
                                placeholder="Search meetings..."
                                left-icon="solar:magnifer-linear"
                                hide-label
                                hide-errors
                                class="tw-w-full"
                            />
                        </div>
                        
                        <!-- View Toggle -->
                        <div class="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-p-1">
                            <button
                                @click="viewMode = 'calendar'"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'calendar' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900'
                                ]"
                            >
                                <icon icon="solar:calendar-bold-duotone" class="tw-text-lg" />
                            </button>
                            <button
                                @click="viewMode = 'list'"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'list' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900'
                                ]"
                            >
                                <icon icon="solar:list-bold-duotone" class="tw-text-lg" />
                            </button>
                        </div>
                        
                        <!-- Schedule Meeting Button -->
                        <a-button v-if="false"
                            @click="scheduleNewMeeting"
                            intent="gradient"
                            left-icon="solar:add-circle-bold-duotone"
                        >
                            Schedule Meeting
                        </a-button>
                        
                    </div>
                    
                    <!-- Header Actions Skeleton -->
                    <div v-else class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-md tw-w-64 tw-animate-pulse"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-md tw-w-20 tw-animate-pulse"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-md tw-w-32 tw-animate-pulse"></div>
                    </div>
                </div>

                <!-- Filters Bar -->
                <div v-if="false" class="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-mb-6 tw-p-4 tw-bg-gray-50 tw-rounded-lg">
                    <!-- Date Range Filter -->
                    <FormField
                        v-model="dateFilter"
                        type="select"
                        hide-label
                        hide-errors
                        left-icon="solar:calendar-linear"
                        class="tw-min-w-[150px]"
                    >
                        <option value="all">All Dates</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="upcoming">Upcoming</option>
                    </FormField>

                    <!-- Status Filter -->
                    <FormField
                        v-model="statusFilter"
                        type="select"
                        hide-label
                        hide-errors
                        left-icon="solar:flag-linear"
                        class="tw-min-w-[150px]"
                    >
                        <option value="all">All Status</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="ongoing">Ongoing</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                    </FormField>

                    <!-- Meeting Type Filter -->
                    <FormField
                        v-model="typeFilter"
                        type="select"
                        hide-label
                        hide-errors
                        left-icon="solar:video-camera-linear"
                        class="tw-min-w-[150px]"
                    >
                        <option value="all">All Types</option>
                        <option value="video">Video Call</option>
                        <option value="in-person">In-Person</option>
                        <option value="phone">Phone Call</option>
                    </FormField>

                    <!-- Clear Filters -->
                    <a-button
                        v-if="hasActiveFilters"
                        @click="clearFilters"
                        intent="ghost"
                        left-icon="solar:refresh-bold-duotone"
                        class="tw-text-sm"
                    >
                        Clear
                    </a-button>
                </div>

                <!-- Content Area -->
                <div class="tw-flex-1">
                    <!-- Loading State -->
                    <div v-if="loading">
                        <!-- Calendar View Skeleton -->
                        <div v-if="viewMode === 'calendar'" class="tw-space-y-4">
                            <!-- Calendar Header Skeleton -->
                            <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
                                <div class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-48 tw-animate-pulse"></div>
                                <div class="tw-flex tw-gap-2">
                                    <div class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-20 tw-animate-pulse"></div>
                                    <div class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-20 tw-animate-pulse"></div>
                                </div>
                            </div>
                            
                            <!-- Calendar Grid Skeleton -->
                            <div class="tw-grid tw-grid-cols-7 tw-gap-1 tw-border tw-border-gray-200 tw-rounded-lg tw-p-4">
                                <!-- Day Headers -->
                                <div v-for="n in 7" :key="n" class="tw-p-2 tw-text-center">
                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                </div>
                                <!-- Calendar Days -->
                                <div v-for="n in 35" :key="n" class="tw-p-2 tw-h-24 tw-border tw-border-gray-100">
                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-6 tw-mb-2 tw-animate-pulse"></div>
                                    <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-full tw-animate-pulse"></div>
                                </div>
                            </div>
                        </div>

                        <!-- List View Skeleton -->
                        <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                            <table class="tw-min-w-full tw-divide-y tw-divide-gray-200 tw-table-fixed">
                                <thead class="tw-bg-gray-50">
                                    <tr>
                                        <th class="tw-w-[35%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Meeting</th>
                                        <th class="tw-w-[18%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Date & Time</th>
                                        <th class="tw-w-[15%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Participants</th>
                                        <th class="tw-w-[12%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Type</th>
                                        <th class="tw-w-[10%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</th>
                                        <th class="tw-w-[10%] tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                    <tr v-for="n in 8" :key="n" class="tw-animate-pulse">
                                        <td class="tw-px-3 tw-py-2">
                                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32"></div>
                                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-24 tw-mt-1"></div>
                                        </td>
                                        <td class="tw-px-3 tw-py-2">
                                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-20"></div>
                                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-16 tw-mt-1"></div>
                                        </td>
                                        <td class="tw-px-3 tw-py-2">
                                            <div class="tw-flex tw-gap-1">
                                                <div class="tw-w-5 tw-h-5 tw-bg-gray-200 tw-rounded-full"></div>
                                                <div class="tw-w-5 tw-h-5 tw-bg-gray-200 tw-rounded-full"></div>
                                            </div>
                                        </td>
                                        <td class="tw-px-3 tw-py-2">
                                            <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-14"></div>
                                        </td>
                                        <td class="tw-px-3 tw-py-2">
                                            <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-16"></div>
                                        </td>
                                        <td class="tw-px-3 tw-py-2 tw-text-right">
                                            <div class="tw-flex tw-items-center tw-justify-end tw-gap-1">
                                                <div class="tw-w-5 tw-h-5 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-5 tw-h-5 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-5 tw-h-5 tw-bg-gray-200 tw-rounded"></div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Empty State -->
                    <div v-else-if="filteredMeetings.length === 0 && !searchQuery && !hasActiveFilters" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:calendar-add-linear" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No meetings scheduled</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Start by scheduling your first meeting</p>
                        <a-button
                            @click="scheduleNewMeeting"
                            intent="primary"
                            left-icon="solar:add-circle-bold-duotone"
                        >
                            Schedule Meeting
                        </a-button>
                    </div>

                    <!-- No Search Results -->
                    <div v-else-if="filteredMeetings.length === 0" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:calendar-search-bold-duotone" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No meetings found</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Try adjusting your search or filters</p>
                        <a-button
                            @click="clearFilters"
                            intent="secondary"
                            left-icon="solar:refresh-bold-duotone"
                        >
                            Clear Filters
                        </a-button>
                    </div>

                    <!-- Calendar View -->
                    <div v-else-if="viewMode === 'calendar'" class="tw-space-y-4">
                        <!-- Calendar Header -->
                        <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
                            <h2 class="tw-text-xl tw-font-semibold tw-text-gray-900">{{ currentMonthYear }}</h2>
                            <div class="tw-flex tw-items-center tw-gap-2">
                                <a-button
                                    @click="previousMonth"
                                    intent="light"
                                    left-icon="solar:arrow-left-linear"
                                    class="tw-text-sm"
                                >
                                    Previous
                                </a-button>
                                <a-button
                                    @click="goToToday"
                                    intent="light"
                                    class="tw-text-sm"
                                >
                                    Today
                                </a-button>
                                <a-button
                                    @click="nextMonth"
                                    intent="light"
                                    right-icon="solar:arrow-right-linear"
                                    class="tw-text-sm"
                                >
                                    Next
                                </a-button>
                            </div>
                        </div>

                        <!-- Calendar Grid -->
                        <div class="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
                            <!-- Day Headers -->
                            <div class="tw-grid tw-grid-cols-7 tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                <div
                                    v-for="day in dayHeaders"
                                    :key="day"
                                    class="tw-p-3 tw-text-center tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                                >
                                    {{ day }}
                                </div>
                            </div>
                            
                            <!-- Calendar Days -->
                            <div class="tw-grid tw-grid-cols-7">
                                <div
                                    v-for="(day, index) in calendarDays"
                                    :key="index"
                                    :class="[
                                        'tw-min-h-[120px] tw-p-2 tw-border-r tw-border-b tw-border-gray-100',
                                        day.isCurrentMonth ? 'tw-bg-white' : 'tw-bg-gray-50',
                                        day.isToday ? 'tw-bg-blue-50' : ''
                                    ]"
                                >
                                                    <!-- Day Number -->
                <div :class="[
                  'tw-text-sm tw-font-medium tw-mb-1',
                  day.isCurrentMonth ? 'tw-text-gray-900' : 'tw-text-gray-400',
                  day.isToday ? 'tw-w-6 tw-h-6 tw-bg-blue-600 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-font-bold' : ''
                ]">
                  {{ day.date }}
                </div>
                                    
                                    <!-- Meetings for this day -->
                                    <div class="tw-space-y-1">
                                        <div
                                            v-for="meeting in day.meetings.slice(0, 2)"
                                            :key="meeting.id"
                                            @click="viewMeeting(meeting)"
                                            :class="[
                                                'tw-text-xs tw-p-1 tw-rounded tw-cursor-pointer tw-truncate',
                                                getStatusColor(meeting.status)
                                            ]"
                                            :title="meeting.title"
                                        >
                                            {{ meeting.time ? meeting.time + ' - ' : '' }}{{ meeting.title }}
                                        </div>
                                        
                                        <!-- More meetings indicator -->
                                        <div
                                            v-if="day.meetings.length > 2"
                                            class="tw-text-xs tw-text-gray-500 tw-cursor-pointer hover:tw-text-gray-700"
                                            @click="viewDayMeetings(day)"
                                        >
                                            +{{ day.meetings.length - 2 }} more
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- List View -->
                    <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                        <table class="tw-min-w-full tw-divide-y tw-divide-gray-200 tw-table-fixed">
                            <thead class="tw-bg-gray-50">
                                <tr>
                                    <th class="tw-w-[35%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Meeting</th>
                                    <th class="tw-w-[18%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Date & Time</th>
                                    <th class="tw-w-[22%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Participants</th>
                                    <th class="tw-w-[12%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</th>
                                    <th class="tw-w-[13%] tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                <tr
                                    v-for="meeting in paginatedMeetings"
                                    :key="meeting.id"
                                    class="hover:tw-bg-gray-50"
                                >
                                    <td class="tw-px-3 tw-py-2">
                                        <div>
                                            <div class="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">{{ meeting.title }}</div>
                                            <div v-if="meeting.description" class="tw-text-xs tw-text-gray-500 tw-truncate">{{ meeting.description }}</div>
                                        </div>
                                    </td>
                                    <td class="tw-px-3 tw-py-2">
                                        <div class="tw-text-xs tw-text-gray-900 tw-whitespace-nowrap">{{ formatCompactDate(meeting.date || meeting.scheduled_at) }}</div>
                                        <div class="tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">{{ meeting.time }}{{ meeting.endTime ? '-' + meeting.endTime : '' }}</div>
                                    </td>
                                    <td class="tw-px-3 tw-py-2">
                                        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                                            <div
                                                v-for="participant in (meeting.participants || [])"
                                                :key="participant.id || participant.user?.id"
                                                :class="[
                                                    'tw-flex tw-items-center tw-gap-1.5 tw-px-2 tw-py-1 tw-rounded-md tw-transition-colors',
                                                    isCompanyUser(participant) 
                                                        ? 'tw-bg-primary-50 tw-border tw-border-primary-200' 
                                                        : 'tw-bg-gray-50'
                                                ]"
                                                :title="participant.user?.name || participant.name"
                                            >
                                                <div class="tw-relative tw-flex-shrink-0">
                                                    <img
                                                        v-if="participant.profile_image || participant.user?.profile_image"
                                                        :src="participant.profile_image || participant.user?.profile_image"
                                                        :alt="participant.user?.name || participant.name"
                                                        class="tw-w-6 tw-h-6 tw-rounded-full tw-object-cover tw-border-2"
                                                        :class="isCompanyUser(participant) ? 'tw-border-primary-400' : 'tw-border-gray-300'"
                                                    />
                                                    <div
                                                        v-else
                                                        :class="[
                                                            'tw-w-6 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-medium tw-border-2',
                                                            isCompanyUser(participant)
                                                                ? 'tw-bg-primary-100 tw-text-primary-700 tw-border-primary-400'
                                                                : 'tw-bg-gray-200 tw-text-gray-600 tw-border-gray-300'
                                                        ]"
                                                    >
                                                        {{ (participant.user?.name || participant.name || '?').charAt(0).toUpperCase() }}
                                                    </div>
                                                    <div
                                                        v-if="participant.user?.is_online || participant.is_online"
                                                        class="tw-absolute tw-bottom-0 tw-right-0 tw-w-2 tw-h-2 tw-rounded-full tw-bg-green-500 tw-border-2 tw-border-white"
                                                    ></div>
                                                </div>
                                                <span
                                                    :class="[
                                                        'tw-text-xs tw-font-medium tw-truncate tw-max-w-[100px]',
                                                        isCompanyUser(participant) ? 'tw-text-primary-700' : 'tw-text-gray-700'
                                                    ]"
                                                >
                                                    {{ participant.user?.name || participant.name }}
                                                </span>
                                                <icon
                                                    v-if="isCompanyUser(participant)"
                                                    icon="solar:buildings-bold-duotone"
                                                    class="tw-text-primary-500 tw-text-xs tw-flex-shrink-0"
                                                />
                                            </div>
                                        </div>
                                    </td>
                                    <td class="tw-px-3 tw-py-2">
                                        <span :class="[
                                            'tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium',
                                            getStatusColor(meeting.status)
                                        ]">
                                            {{ formatMeetingStatus(meeting.status) }}
                                        </span>
                                    </td>
                                    <td class="tw-px-3 tw-py-2 tw-text-right">
                                        <button
                                            @click="viewMeeting(meeting)"
                                            class="tw-text-gray-400 hover:tw-text-primary-600 tw-transition-colors"
                                            :title="$t('buttons.view', 'View')"
                                        >
                                            <icon icon="solar:eye-bold-duotone" class="tw-text-base" />
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="viewMode === 'list' && computedTotalPages > 1" class="tw-flex tw-items-center tw-justify-between tw-mt-6">
                        <div class="tw-text-sm tw-text-gray-600">
                            <span v-if="totalMeetings">
                                Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} to {{ Math.min(currentPage * itemsPerPage, totalMeetings) }} of {{ totalMeetings }} meetings
                            </span>
                            <span v-else>
                                Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} to {{ Math.min(currentPage * itemsPerPage, filteredMeetings.length) }} of {{ filteredMeetings.length }} meetings
                            </span>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <a-button
                                @click="loadData(currentPage - 1)"
                                :disabled="currentPage === 1"
                                intent="light"
                                left-icon="solar:arrow-left-linear"
                            >
                                Previous
                            </a-button>
                            <a-button
                                @click="loadData(currentPage + 1)"
                                :disabled="currentPage === computedTotalPages"
                                intent="light"
                                right-icon="solar:arrow-right-linear"
                            >
                                Next
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Right Sidebar - Stats -->
        <div class="tw-col-span-12 lg:tw-col-span-3">
            <div class="tw-space-y-3">
                <!-- Your Stats Card -->
                <div class="tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200">
                    <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
                        <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">Your stats</h3>
                        <icon icon="solar:settings-linear" class="tw-text-sm tw-text-gray-400" />
                    </div>
                    
                    <div class="tw-space-y-2">
                        <!-- Scheduled Meetings -->
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <div class="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-green-100 tw-flex tw-items-center tw-justify-center">
                                <icon icon="solar:calendar-add-bold" class="tw-text-sm tw-text-green-600" />
                            </div>
                            <div>
                                <div class="tw-text-sm tw-font-medium tw-text-gray-900">{{ scheduledCount }} Scheduled meetings</div>
                            </div>
                        </div>
                        
                        <!-- Completed Meetings -->
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <div class="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-green-100 tw-flex tw-items-center tw-justify-center">
                                <icon icon="solar:calendar-mark-bold" class="tw-text-sm tw-text-green-600" />
                            </div>
                            <div>
                                <div class="tw-text-sm tw-font-medium tw-text-gray-900">{{ completedCount }} Completed meetings</div>
                            </div>
                        </div>
                        
                        <!-- Ongoing Meetings -->
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <div class="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-orange-100 tw-flex tw-items-center tw-justify-center">
                                <icon icon="solar:calendar-bold" class="tw-text-sm tw-text-orange-600" />
                            </div>
                            <div>
                                <div class="tw-text-sm tw-font-medium tw-text-gray-900">{{ ongoingCount }} Ongoing meetings</div>
                            </div>
                        </div>
                        
                        <!-- Cancelled Meetings -->
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <div class="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-red-100 tw-flex tw-items-center tw-justify-center">
                                <icon icon="solar:close-circle-bold" class="tw-text-sm tw-text-red-600" />
                            </div>
                            <div>
                                <div class="tw-text-sm tw-font-medium tw-text-gray-900">{{ cancelledCount }} Cancelled meetings</div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Export Card -->
                <div class="tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200">
                    <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-1">Download your meetings summary</h3>
                    <p class="tw-text-xs tw-text-gray-600 tw-mb-3">Get all meetings in an excel file</p>
                    
                    <a-button
                        @click="exportMeetings"
                        intent="secondary"
                        left-icon="solar:download-bold"
                        class="tw-w-full tw-justify-start tw-text-sm tw-py-2"
                    >
                        Export meetings
                    </a-button>
                </div>
            </div>
        </div>
    </div>

    <!-- Meeting Details Popup -->
    <a-popup :visible="showMeetingPopup" @cancel="closeMeetingPopup">
        <div v-if="selectedMeeting" class="tw-mx-auto tw-w-[95%] md:tw-max-w-2xl tw-bg-white tw-rounded-2xl tw-shadow-xl tw-overflow-hidden">
            <!-- Header -->
            <div class="tw-relative tw-bg-gradient-to-br tw-from-primary-50 tw-to-primary-100/50 tw-px-6 tw-pt-8 tw-pb-6">
                <div class="tw-absolute tw-top-4 tw-right-4">
                    <button @click="closeMeetingPopup" class="tw-p-2 tw-rounded-full hover:tw-bg-white/30 tw-transition-colors">
                        <icon icon="solar:close-circle-bold" class="tw-text-xl tw-text-gray-600" />
                    </button>
                </div>
                
                <div class="tw-flex tw-items-center tw-gap-3">
                    <div class="tw-p-2 tw-bg-white/80 tw-rounded-xl">
                        <icon icon="solar:calendar-bold-duotone" class="tw-text-2xl tw-text-primary-500" />
                    </div>
                    <div class="tw-flex-1 tw-min-w-0">
                        <h2 class="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-2 tw-line-clamp-2">
                            {{ selectedMeeting.title }}
                        </h2>
                        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                            <span :class="[
                                'tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full',
                                getStatusColor(selectedMeeting.status)
                            ]">
                                {{ formatMeetingStatus(selectedMeeting.status) }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Content -->
            <div class="tw-p-6 tw-space-y-6">
                <!-- Date & Time -->
                <div class="tw-space-y-3">
                    <div class="tw-flex tw-items-start tw-gap-3">
                        <icon icon="solar:calendar-bold-duotone" class="tw-text-primary-500 tw-mt-0.5 tw-shrink-0" />
                        <div class="tw-flex-1">
                            <div class="tw-font-medium tw-text-gray-900 tw-mb-1">
                                {{ formatCompactDate(selectedMeeting.date || selectedMeeting.scheduled_at) }}
                            </div>
                            <div class="tw-text-sm tw-text-gray-600">
                                {{ selectedMeeting.time }}{{ selectedMeeting.endTime ? ' - ' + selectedMeeting.endTime : '' }}
                                <span v-if="selectedMeeting.duration_minutes" class="tw-ml-2 tw-text-gray-500">
                                    ({{ formatDuration(selectedMeeting.duration_minutes) }})
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Description -->
                <div v-if="selectedMeeting.description" class="tw-space-y-2">
                    <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">{{ $t('fields.description') }}</h3>
                    <p class="tw-text-sm tw-text-gray-600 tw-leading-relaxed">{{ selectedMeeting.description }}</p>
                </div>

                <!-- Participants -->
                <div v-if="selectedMeeting.participants && selectedMeeting.participants.length" class="tw-space-y-3">
                    <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">
                        {{ $t('common.participants', 'Participants') }} ({{ selectedMeeting.participants.length }})
                    </h3>
                    <div class="tw-space-y-2">
                        <div
                            v-for="participant in selectedMeeting.participants"
                            :key="participant.id || participant.user?.id"
                            :class="[
                                'tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-transition-colors',
                                isCompanyUser(participant) 
                                    ? 'tw-bg-primary-50 tw-border tw-border-primary-200' 
                                    : 'tw-bg-gray-50'
                            ]"
                        >
                            <div class="tw-relative tw-flex-shrink-0">
                                <img
                                    v-if="participant.profile_image || participant.user?.profile_image"
                                    :src="participant.profile_image || participant.user?.profile_image"
                                    :alt="participant.user?.name || participant.name"
                                    class="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover tw-border-2"
                                    :class="isCompanyUser(participant) ? 'tw-border-primary-400' : 'tw-border-gray-300'"
                                />
                                <div
                                    v-else
                                    :class="[
                                        'tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-medium tw-border-2',
                                        isCompanyUser(participant)
                                            ? 'tw-bg-primary-100 tw-text-primary-700 tw-border-primary-400'
                                            : 'tw-bg-gray-200 tw-text-gray-600 tw-border-gray-300'
                                    ]"
                                >
                                    {{ (participant.user?.name || participant.name || '?').charAt(0).toUpperCase() }}
                                </div>
                                <div
                                    v-if="participant.user?.is_online || participant.is_online"
                                    class="tw-absolute tw-bottom-0 tw-right-0 tw-w-3 tw-h-3 tw-rounded-full tw-bg-green-500 tw-border-2 tw-border-white"
                                ></div>
                            </div>
                            <div class="tw-flex-1 tw-min-w-0">
                                <p :class="[
                                    'tw-text-sm tw-font-medium tw-text-gray-900',
                                    isCompanyUser(participant) ? 'tw-text-primary-700' : ''
                                ]">
                                    {{ participant.user?.name || participant.name }}
                                </p>
                                <p v-if="participant.role" class="tw-text-xs tw-text-gray-500 tw-capitalize">
                                    {{ participant.role }}
                                </p>
                            </div>
                            <icon
                                v-if="isCompanyUser(participant)"
                                icon="solar:buildings-bold-duotone"
                                class="tw-text-primary-500 tw-text-base tw-flex-shrink-0"
                            />
                        </div>
                    </div>
                </div>

                <!-- Meeting URL (if available) -->
                <div v-if="getMeetingUrl(selectedMeeting)" class="tw-space-y-2">
                    <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">
                        {{ isOrganizer(selectedMeeting) ? $t('common.organizer-link', 'Organizer Link') : $t('common.meeting-link', 'Meeting Link') }}
                    </h3>
                    <div class="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200">
                        <a
                            :href="getMeetingUrl(selectedMeeting)"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="tw-flex-1 tw-text-sm tw-text-primary-600 hover:tw-text-primary-700 tw-underline tw-truncate"
                            :title="getMeetingUrl(selectedMeeting)"
                        >
                            {{ getMeetingUrl(selectedMeeting) }}
                        </a>
                        <button
                            @click="copyMeetingUrl(selectedMeeting)"
                            class="tw-p-1.5 tw-rounded-md tw-bg-white tw-border tw-border-gray-300 hover:tw-bg-gray-100 tw-transition-colors tw-flex-shrink-0"
                            :title="$t('buttons.copy', 'Copy')"
                        >
                            <icon 
                                :icon="copiedUrl ? 'solar:check-circle-bold-duotone' : 'solar:copy-bold-duotone'" 
                                :class="[
                                    'tw-text-base tw-transition-colors',
                                    copiedUrl ? 'tw-text-green-600' : 'tw-text-gray-600'
                                ]"
                            />
                        </button>
                    </div>
                    <p v-if="isOrganizer(selectedMeeting)" class="tw-text-xs tw-text-gray-500 tw-mt-1">
                        {{ $t('common.organizer-link-description', 'You are the organizer. Use this link to host the meeting.') }}
                    </p>
                </div>
            </div>

            <!-- Footer -->
            <div class="tw-p-6 tw-bg-gray-50 tw-flex tw-items-center tw-justify-end tw-gap-3">
                <a-button intent="white" @click="closeMeetingPopup">
                    {{ $t('buttons.close', 'Close') }}
                </a-button>
                <a-button
                    v-if="getMeetingUrl(selectedMeeting)"
                    intent="primary"
                    right-icon="solar:arrow-right-outline"
                    @click="joinMeeting(selectedMeeting)"
                >
                    {{ isOrganizer(selectedMeeting) ? $t('buttons.start-meeting', 'Start Meeting') : $t('buttons.join-meeting', 'Join Meeting') }}
                </a-button>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import FormField from '@/components/form/FormField.vue'
import useAlert from '@/composables/useAlert'
import myCompanyApi from '@/api/my-company'
import { useAuthStore } from '@/stores'

// Data
const loading = ref(false)
const searchQuery = ref('')
const viewMode = ref('calendar')
const currentPage = ref(1)
const itemsPerPage = ref(10)
const totalPages = ref(1)

// Filters
const dateFilter = ref('all')
const statusFilter = ref('all')
const typeFilter = ref('all')

// Calendar state
const currentDate = ref(new Date())

// Meetings data
const meetings = ref([])
const totalMeetings = ref(0)

// Auth store
const authStore = useAuthStore()

// Helper function to check if participant is from our company
const isCompanyUser = (participant) => {
    const currentCompany = authStore.company
    if (!currentCompany || !currentCompany.id) {
        return false
    }
    
    // Check if participant's user has a company_id matching our company
    const participantCompanyId = participant.user?.company_id || participant.company_id
    if (participantCompanyId && participantCompanyId === currentCompany.id) {
        return true
    }
    
    // Check if participant is the organizer (organizer is usually a company user)
    // This is a fallback check
    return false
}

// Helper function to check if current user is the organizer
const isOrganizer = (meeting) => {
    const currentUser = authStore.user
    if (!currentUser || !currentUser.id) {
        return false
    }
    
    // Check if organizer exists and matches current user
    if (meeting.organizer) {
        // Handle different organizer structures
        const organizerId = meeting.organizer.id || meeting.organizer.user_id || meeting.organizer.user?.id
        if (organizerId && organizerId === currentUser.id) {
            return true
        }
        
        // Also check if organizer is a user object directly
        if (meeting.organizer.user && meeting.organizer.user.id === currentUser.id) {
            return true
        }
    }
    
    return false
}

// Get the appropriate meeting URL (host URL if organizer, regular URL otherwise)
const getMeetingUrl = (meeting) => {
    if (isOrganizer(meeting) && meeting.host_room_url) {
        return meeting.host_room_url
    }
    return meeting.room_url || meeting.meeting_url
}

// Computed properties
const hasActiveFilters = computed(() => {
    return dateFilter.value !== 'all' || statusFilter.value !== 'all' || typeFilter.value !== 'all' || searchQuery.value
})

const filteredMeetings = computed(() => {
    let filtered = [...meetings.value]

    // Search filter (client-side if not handled by API)
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(meeting =>
            meeting.title?.toLowerCase().includes(query) ||
            meeting.description?.toLowerCase().includes(query) ||
            meeting.participants?.some(p => {
                const name = p.user?.name || p.name || ''
                return name.toLowerCase().includes(query)
            })
        )
    }

    // Date filter
    if (dateFilter.value !== 'all') {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const startOfWeek = new Date(today)
        startOfWeek.setDate(today.getDate() - today.getDay())
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

        filtered = filtered.filter(meeting => {
            const meetingDate = meeting.date
            if (!meetingDate) return false
            
            const meetingDateOnly = new Date(meetingDate)
            meetingDateOnly.setHours(0, 0, 0, 0)
            
            switch (dateFilter.value) {
                case 'today':
                    return meetingDateOnly.toDateString() === today.toDateString()
                case 'week':
                    return meetingDateOnly >= startOfWeek && meetingDateOnly <= new Date(startOfWeek.getTime() + 7 * 24 * 60 * 60 * 1000)
                case 'month':
                    return meetingDateOnly >= startOfMonth && meetingDateOnly < new Date(today.getFullYear(), today.getMonth() + 1, 1)
                case 'upcoming':
                    return meetingDateOnly >= today
                default:
                    return true
            }
        })
    }

    // Status filter
    if (statusFilter.value !== 'all') {
        filtered = filtered.filter(meeting => {
            // Map API status to display status
            const status = meeting.status
            if (statusFilter.value === 'scheduled') {
                return status === 'pending' || status === 'accepted' || status === 'scheduled'
            }
            return status === statusFilter.value
        })
    }

    // Type filter
    if (typeFilter.value !== 'all') {
        filtered = filtered.filter(meeting => {
            const type = meeting.type || meeting.meeting_type || ''
            const normalizedType = type.toLowerCase().replace('_', '-').replace(' ', '-')
            return normalizedType === typeFilter.value
        })
    }

    return filtered
})

const paginatedMeetings = computed(() => {
    if (viewMode.value !== 'list') return filteredMeetings.value
    
    const start = (currentPage.value - 1) * itemsPerPage.value
    const end = start + itemsPerPage.value
    return filteredMeetings.value.slice(start, end)
})

const computedTotalPages = computed(() => {
    // Use API pagination if available, otherwise calculate from filtered results
    if (totalPages.value > 1) {
        return totalPages.value
    }
    return Math.ceil(filteredMeetings.value.length / itemsPerPage.value)
})

// Stats computed properties
const scheduledCount = computed(() => {
    return meetings.value.filter(meeting => 
        meeting.status === 'scheduled' || 
        meeting.status === 'pending' || 
        meeting.status === 'accepted'
    ).length
})

const completedCount = computed(() => {
    return meetings.value.filter(meeting => meeting.status === 'completed').length
})

const ongoingCount = computed(() => {
    return meetings.value.filter(meeting => meeting.status === 'in_progress' || meeting.status === 'ongoing').length
})

const cancelledCount = computed(() => {
    return meetings.value.filter(meeting => meeting.status === 'cancelled' || meeting.status === 'declined').length
})

// Calendar computed properties
const currentMonthYear = computed(() => {
    return currentDate.value.toLocaleDateString('en-US', { 
        month: 'long', 
        year: 'numeric' 
    })
})

const dayHeaders = computed(() => {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
})

const calendarDays = computed(() => {
    const year = currentDate.value.getFullYear()
    const month = currentDate.value.getMonth()
    const firstDay = new Date(year, month, 1)
    const startDate = new Date(firstDay)
    startDate.setDate(startDate.getDate() - firstDay.getDay())
    
    const days = []
    const today = new Date()
    
    for (let i = 0; i < 42; i++) {
        const date = new Date(startDate)
        date.setDate(startDate.getDate() + i)
        
        const dayMeetings = filteredMeetings.value.filter(meeting => {
            if (!meeting.date) return false
            const meetingDate = new Date(meeting.date)
            meetingDate.setHours(0, 0, 0, 0)
            const compareDate = new Date(date)
            compareDate.setHours(0, 0, 0, 0)
            return meetingDate.toDateString() === compareDate.toDateString()
        })
        
        days.push({
            date: date.getDate(),
            fullDate: date,
            isCurrentMonth: date.getMonth() === month,
            isToday: date.toDateString() === today.toDateString(),
            meetings: dayMeetings
        })
    }
    
    return days
})

// Methods
const clearFilters = () => {
    dateFilter.value = 'all'
    statusFilter.value = 'all'
    typeFilter.value = 'all'
    searchQuery.value = ''
    currentPage.value = 1
}

const scheduleNewMeeting = () => {
    console.log('Schedule new meeting')
    useAlert('Meeting scheduling feature coming soon!', 'info')
}

const selectedMeeting = ref(null)
const showMeetingPopup = ref(false)
const copiedUrl = ref(false)

const viewMeeting = (meeting) => {
    selectedMeeting.value = meeting
    showMeetingPopup.value = true
    copiedUrl.value = false
}

const closeMeetingPopup = () => {
    showMeetingPopup.value = false
    selectedMeeting.value = null
    copiedUrl.value = false
}

const copyMeetingUrl = async (meeting) => {
    const url = getMeetingUrl(meeting)
    if (!url) return
    
    try {
        await navigator.clipboard.writeText(url)
        copiedUrl.value = true
        useAlert('Link copied to clipboard!', 'success')
        setTimeout(() => {
            copiedUrl.value = false
        }, 2000)
    } catch (err) {
        console.error('Failed to copy text:', err)
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = url
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.select()
        try {
            document.execCommand('copy')
            copiedUrl.value = true
            useAlert('Link copied to clipboard!', 'success')
            setTimeout(() => {
                copiedUrl.value = false
            }, 2000)
        } catch (fallbackErr) {
            console.error('Fallback copy failed:', fallbackErr)
            useAlert('Failed to copy link', 'danger')
        }
        document.body.removeChild(textArea)
    }
}

const joinMeeting = (meeting) => {
    const meetingUrl = getMeetingUrl(meeting)
    if (meetingUrl) {
        window.open(meetingUrl, '_blank', 'noopener,noreferrer')
    } else {
        useAlert('Meeting link not available', 'warning')
    }
}

const formatDuration = (minutes) => {
    if (!minutes) return ''
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    
    if (hours > 0) {
        return `${hours}h ${remainingMinutes > 0 ? remainingMinutes + 'm' : ''}`
    }
    return `${minutes}m`
}




const viewDayMeetings = (day) => {
    console.log('View meetings for day:', day.fullDate)
    useAlert(`Viewing ${day.meetings.length} meetings for ${day.fullDate.toDateString()}`, 'info')
}

const exportMeetings = () => {
    console.log('Exporting meetings to Excel')
    useAlert('Meetings exported successfully!', 'success')
    // In a real app, this would generate and download an Excel file
}

// Calendar navigation
const previousMonth = () => {
    currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() - 1, 1)
}

const nextMonth = () => {
    currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 1)
}

const goToToday = () => {
    currentDate.value = new Date()
}

// Utility methods
const formatCompactDate = (date) => {
  try {
    // Check if the date is a string with timezone indicator
    if (typeof date === 'string') {
      const dateObj = new Date(date);
      const isUTC = date.includes('Z');
      
      if (isUTC) {
        // For UTC dates, use UTC methods to avoid timezone conversion
        const month = dateObj.toLocaleString('en-US', { month: 'short' });
        const day = dateObj.getUTCDate();
        return `${month} ${day}`;
      } else {
        // For local dates, use local methods
        return dateObj.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
      }
    } else {
      // If date is already a Date object, use local methods
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  } catch (error) {
    console.error('Error formatting compact date:', error);
    return 'Invalid Date';
  }
}

const formatMeetingStatus = (status) => {
    const statusMap = {
        'pending': 'Pending',
        'accepted': 'Accepted',
        'declined': 'Declined',
        'scheduled': 'Scheduled',
        'in_progress': 'In Progress',
        'ongoing': 'Ongoing',
        'completed': 'Completed',
        'cancelled': 'Cancelled'
    }
    return statusMap[status] || status
}


const getStatusColor = (status) => {
    switch (status) {
        case 'scheduled':
        case 'pending':
        case 'accepted':
            return 'tw-bg-blue-100 tw-text-blue-800'
        case 'ongoing':
        case 'in_progress':
            return 'tw-bg-green-100 tw-text-green-800'
        case 'completed':
            return 'tw-bg-gray-100 tw-text-gray-800'
        case 'cancelled':
        case 'declined':
            return 'tw-bg-red-100 tw-text-red-800'
        default:
            return 'tw-bg-gray-100 tw-text-gray-800'
    }
}



// Helper function to format time from date string
const formatTime = (dateString) => {
    if (!dateString) return ''
    try {
        const date = new Date(dateString)
        const hours = date.getHours()
        const minutes = date.getMinutes()
        const ampm = hours >= 12 ? 'PM' : 'AM'
        const displayHours = hours % 12 || 12
        const displayMinutes = minutes.toString().padStart(2, '0')
        return `${displayHours}:${displayMinutes} ${ampm}`
    } catch (error) {
        return ''
    }
}

// Helper function to calculate end time
const calculateEndTime = (startDate, durationMinutes) => {
    if (!startDate || !durationMinutes) return null
    try {
        const start = new Date(startDate)
        const end = new Date(start.getTime() + durationMinutes * 60000)
        return formatTime(end.toISOString())
    } catch (error) {
        return null
    }
}

const loadData = async (page = 1) => {
    loading.value = true

    try {
        const params = {
            page: page || currentPage.value,
            per_page: itemsPerPage.value
        }
        
        // Add search query if provided
        if (searchQuery.value && searchQuery.value.trim()) {
            params.search = searchQuery.value.trim()
        }
        
        // Add status filter if provided
        if (statusFilter.value !== 'all') {
            params.status = statusFilter.value
        }
        
        // Add type filter if provided
        if (typeFilter.value !== 'all') {
            params.type = typeFilter.value.replace('-', '_')
        }
        
        // Add upcoming filter if date filter is 'upcoming'
        if (dateFilter.value === 'upcoming') {
            params.upcoming = true
        }

        const response = await myCompanyApi.getMeetings(params)
        const responseData = response.data

        // Handle the API response structure: { code, data: { data: [], current_page, last_page, total } }
        if (responseData?.data?.data && Array.isArray(responseData.data.data)) {
            // Map meetings from API response
            meetings.value = responseData.data.data.map(meeting => {
                const scheduledDate = meeting.scheduled_at ? new Date(meeting.scheduled_at) : null
                const endTime = calculateEndTime(meeting.scheduled_at, meeting.duration_minutes)
                
                return {
                    ...meeting,
                    id: meeting.id,
                    title: meeting.title,
                    description: meeting.description,
                    // Convert scheduled_at to date object for calendar
                    date: scheduledDate,
                    // Format time for display
                    time: scheduledDate ? formatTime(meeting.scheduled_at) : '',
                    endTime: endTime,
                    // Map status
                    status: meeting.status,
                    // Map type
                    type: meeting.meeting_type || meeting.type,
                    meeting_type: meeting.meeting_type || meeting.type,
                    // Map participants - each participant has a user object
                    participants: (meeting.participants || []).map(participant => ({
                        ...participant,
                        id: participant.user?.id || participant.user_id || participant.id,
                        name: participant.user?.name || participant.name || 'Unknown',
                        profile_image: participant.user?.profile_image,
                        role: participant.role,
                        status: participant.status,
                        company_id: participant.user?.company_id || participant.company_id,
                        is_online: participant.user?.is_online || participant.is_online
                    })),
                    // Store organizer info
                    organizer: meeting.organizer,
                    // Store user info (for member_to_member meetings)
                    user: meeting.user,
                    // Store company info (for member_to_company meetings)
                    company: meeting.company,
                    // Store original scheduled_at for API compatibility
                    scheduled_at: meeting.scheduled_at,
                    duration_minutes: meeting.duration_minutes,
                    location: meeting.location,
                    room_url: meeting.room_url,
                    host_room_url: meeting.host_room_url,
                    meeting_url: meeting.meeting_url || meeting.room_url
                }
            })
            
            // Update pagination info
            if (responseData.data.current_page) {
                currentPage.value = responseData.data.current_page
                totalMeetings.value = responseData.data.total || 0
                // Update totalPages for pagination
                totalPages.value = responseData.data.last_page || 1
            } else {
                totalMeetings.value = meetings.value.length
            }
        } else if (responseData?.data && Array.isArray(responseData.data)) {
            // Fallback: if data is directly an array
            meetings.value = responseData.data.map(meeting => {
                const scheduledDate = meeting.scheduled_at ? new Date(meeting.scheduled_at) : null
                return {
                    ...meeting,
                    date: scheduledDate,
                    time: scheduledDate ? formatTime(meeting.scheduled_at) : '',
                    endTime: calculateEndTime(meeting.scheduled_at, meeting.duration_minutes),
                    participants: (meeting.participants || []).map(p => ({
                        ...p,
                        id: p.user?.id || p.user_id || p.id,
                        name: p.user?.name || p.name || 'Unknown',
                        profile_image: p.user?.profile_image,
                        company_id: p.user?.company_id || p.company_id,
                        is_online: p.user?.is_online || p.is_online
                    }))
                }
            })
            totalMeetings.value = responseData.total || meetings.value.length
        } else if (Array.isArray(responseData)) {
            // Simple array response
            meetings.value = responseData.map(meeting => {
                const scheduledDate = meeting.scheduled_at ? new Date(meeting.scheduled_at) : null
                return {
                    ...meeting,
                    date: scheduledDate,
                    time: scheduledDate ? formatTime(meeting.scheduled_at) : '',
                    endTime: calculateEndTime(meeting.scheduled_at, meeting.duration_minutes),
                    participants: (meeting.participants || []).map(p => ({
                        ...p,
                        id: p.user?.id || p.user_id || p.id,
                        name: p.user?.name || p.name || 'Unknown',
                        profile_image: p.user?.profile_image,
                        company_id: p.user?.company_id || p.company_id,
                        is_online: p.user?.is_online || p.is_online
                    }))
                }
            })
            totalMeetings.value = responseData.length
        } else {
            meetings.value = []
            totalMeetings.value = 0
        }
    } catch (error) {
        console.error('Error loading meetings:', error)
        useAlert('Failed to load meetings', 'danger')
        meetings.value = []
        totalMeetings.value = 0
    } finally {
        loading.value = false
    }
}

// Watch for search query changes (debounced)
let searchTimeout = null
watch(searchQuery, () => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        currentPage.value = 1
        loadData()
    }, 500)
})

// Watch for filter changes
watch([statusFilter, typeFilter, dateFilter], () => {
    currentPage.value = 1
    loadData()
})

// Initialize
onMounted(() => {
    loadData()
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style> 